import { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  RotateCw, 
  FlipHorizontal, 
  Crop, 
  Check, 
  X, 
  Undo2, 
  Image as ImageIcon,
  SunMedium,
  Contrast
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface ImageEditorProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  onSave: (newUrl: string) => void;
}

const ImageEditor = ({ isOpen, onClose, imageUrl, onSave }: ImageEditorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [rotation, setRotation] = useState(0);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [filter, setFilter] = useState<'none' | 'grayscale' | 'bw'>('none');
  const [watermarkText, setWatermarkText] = useState('');
  const [watermarkColor, setWatermarkColor] = useState('#ffffff');
  
  // Load image
  useEffect(() => {
    if (imageUrl && isOpen) {
        const img = new Image();
        img.src = imageUrl;
        img.crossOrigin = "anonymous";
        img.onload = () => setImage(img);
        
        // Reset states
        setRotation(0);
        setBrightness(100);
        setContrast(100);
        setFilter('none');
        setWatermarkText('');
    }
  }, [imageUrl, isOpen]);

  // Render to canvas
  useEffect(() => {
    if (!image || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Handle Rotation Dimensions
    const isVertical = rotation % 180 !== 0;
    canvas.width = isVertical ? image.height : image.width;
    canvas.height = isVertical ? image.width : image.height;

    // Clear
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Apply Transforms
    ctx.save();
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) ${filter === 'grayscale' ? 'grayscale(100%)' : ''} ${filter === 'bw' ? 'grayscale(100%) contrast(200%)' : ''}`;
    
    ctx.drawImage(
        image, 
        -image.width / 2, 
        -image.height / 2
    );
    ctx.restore();

    // Apply Watermark
    if (watermarkText) {
        ctx.save();
        ctx.font = `bold ${Math.max(20, canvas.width / 20)}px Arial`;
        ctx.fillStyle = watermarkColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(0,0,0,0.5)';
        ctx.shadowBlur = 4;
        ctx.fillText(watermarkText, canvas.width / 2, canvas.height / 2);
        ctx.restore();
    }

  }, [image, rotation, brightness, contrast, filter, watermarkText, watermarkColor]);

  const handleSave = () => {
    if (canvasRef.current) {
        onSave(canvasRef.current.toDataURL('image/jpeg', 0.9));
        onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 overflow-hidden bg-zinc-950 border-zinc-800 text-white">
        <DialogHeader className="p-4 border-b border-zinc-800 bg-zinc-900/50">
          <DialogTitle className="flex items-center gap-2">
             <ImageIcon className="w-5 h-5 text-indigo-400" />
             Edit Image
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 flex flex-col lg:flex-row min-h-0 bg-black/50">
           {/* Canvas Preview Area */}
           <div className="flex-1 p-8 flex items-center justify-center overflow-auto relative bg-[url('https://media.istockphoto.com/id/1144604909/vector/transparent-pattern-seamless-grid-background-transparency-grid-texture-gray-squares-vector.jpg?s=612x612&w=0&k=20&c=wXCDk1Jb7V7l7Hn3_8jP8lXoT8XwV1g_8lK8V1g_8lK8Vw=')] bg-repeat bg-[length:20px_20px]">
              <canvas ref={canvasRef} className="max-w-full max-h-full shadow-2xl border border-zinc-700" />
           </div>

           {/* Controls Sidebar */}
           <div className="w-full lg:w-80 bg-zinc-900 border-l border-zinc-800 flex flex-col">
              <Tabs defaultValue="adjust" className="flex-1 flex flex-col">
                 <TabsList className="grid w-full grid-cols-3 bg-zinc-950 p-1 rounded-none border-b border-zinc-800">
                    <TabsTrigger value="adjust" className="data-[state=active]:bg-zinc-800 text-zinc-400">Adjust</TabsTrigger>
                    <TabsTrigger value="filters" className="data-[state=active]:bg-zinc-800 text-zinc-400">Filters</TabsTrigger>
                    <TabsTrigger value="watermark" className="data-[state=active]:bg-zinc-800 text-zinc-400">Text</TabsTrigger>
                 </TabsList>

                 <div className="p-6 flex-1 space-y-8 overflow-y-auto">
                    {/* ... (Adjust Content) ... */} {/* I will need to be careful with replace here to not delete existing content if I can't match exact lines easily. I'll use a larger block or careful targeting. */}

                    <TabsContent value="watermark" className="mt-0 space-y-6">
                        <div className="space-y-3">
                           <Label className="text-xs font-bold uppercase text-zinc-500 tracking-wider">Overlay Text</Label>
                           <Input 
                             placeholder="Enter watermark text..." 
                             className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-600"
                             value={watermarkText}
                             onChange={(e) => setWatermarkText(e.target.value)}
                           />
                        </div>
                        <div className="space-y-3">
                           <Label className="text-xs font-bold uppercase text-zinc-500 tracking-wider">Color</Label>
                           <div className="flex gap-2">
                              {['#ffffff', '#000000', '#ff0000', '#ffff00'].map(c => (
                                 <button 
                                   key={c}
                                   onClick={() => setWatermarkColor(c)}
                                   className={`w-8 h-8 rounded-full border-2 ${watermarkColor === c ? 'border-indigo-500' : 'border-transparent'}`}
                                   style={{ backgroundColor: c }}
                                 />
                              ))}
                           </div>
                        </div>
                    </TabsContent>
                    <TabsContent value="adjust" className="space-y-6 mt-0">
                       {/* Rotation */}
                       <div className="space-y-3">
                          <label className="text-xs font-bold uppercase text-zinc-500 tracking-wider">Rotation</label>
                          <div className="flex gap-2">
                             <Button 
                               variant="outline" 
                               className="flex-1 bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
                               onClick={() => setRotation(r => r - 90)}
                             >
                                <Undo2 className="w-4 h-4 mr-2" /> -90°
                             </Button>
                             <Button 
                               variant="outline" 
                               className="flex-1 bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
                               onClick={() => setRotation(r => r + 90)}
                             >
                                <RotateCw className="w-4 h-4 mr-2" /> +90°
                             </Button>
                          </div>
                       </div>

                       {/* Brightness */}
                       <div className="space-y-3">
                          <div className="flex justify-between">
                             <label className="text-xs font-bold uppercase text-zinc-500 tracking-wider flex items-center gap-2">
                                <SunMedium className="w-3 h-3" /> Brightness
                             </label>
                             <span className="text-xs font-mono text-zinc-400">{brightness}%</span>
                          </div>
                          <Slider 
                            value={[brightness]} 
                            onValueChange={(v) => setBrightness(v[0])} 
                            min={0} max={200} step={5}
                            className="py-2"
                          />
                       </div>

                       {/* Contrast */}
                       <div className="space-y-3">
                          <div className="flex justify-between">
                             <label className="text-xs font-bold uppercase text-zinc-500 tracking-wider flex items-center gap-2">
                                <Contrast className="w-3 h-3" /> Contrast
                             </label>
                             <span className="text-xs font-mono text-zinc-400">{contrast}%</span>
                          </div>
                          <Slider 
                            value={[contrast]} 
                            onValueChange={(v) => setContrast(v[0])} 
                            min={0} max={200} step={5}
                             className="py-2"
                          />
                       </div>
                    </TabsContent>

                    <TabsContent value="filters" className="mt-0">
                       <div className="grid grid-cols-2 gap-4">
                          <button 
                            onClick={() => setFilter('none')}
                            className={`aspect-video rounded-lg border-2 flex flex-col items-center justify-center gap-2 transition-all ${filter === 'none' ? 'border-indigo-500 bg-indigo-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                          >
                             <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-pink-400" />
                             <span className="text-xs font-bold">Original</span>
                          </button>

                          <button 
                            onClick={() => setFilter('grayscale')}
                            className={`aspect-video rounded-lg border-2 flex flex-col items-center justify-center gap-2 transition-all ${filter === 'grayscale' ? 'border-indigo-500 bg-indigo-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                          >
                             <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600" />
                             <span className="text-xs font-bold">Grayscale</span>
                          </button>

                          <button 
                            onClick={() => setFilter('bw')}
                            className={`aspect-video rounded-lg border-2 flex flex-col items-center justify-center gap-2 transition-all ${filter === 'bw' ? 'border-indigo-500 bg-indigo-500/10' : 'border-zinc-700 hover:border-zinc-500'}`}
                          >
                             <div className="w-8 h-8 rounded-full bg-gradient-to-br from-gray-200 to-black border border-gray-600" />
                             <span className="text-xs font-bold">Black & White</span>
                          </button>
                       </div>
                    </TabsContent>
                 </div>

                 {/* Actions Footer in Sidebar */}
                 <div className="p-6 border-t border-zinc-800 bg-zinc-900/50 space-y-3">
                    <Button onClick={handleSave} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white">
                       <Check className="w-4 h-4 mr-2" /> Apply Changes
                    </Button>
                    <Button onClick={onClose} variant="ghost" className="w-full text-zinc-400 hover:text-white hover:bg-zinc-800">
                       Cancel
                    </Button>
                 </div>
              </Tabs>
           </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageEditor;
